/**
 * Sequence formats (ADR 0036): the canvas sizes and frame rates PRISM's Sequence editor offers, mirrored from
 * PRISM's sequence-formats contract so a graphic is authored in the shape PRISM lays out natively. PRISM reads a
 * package's size from renderRequirements[0].resolution; a size that matches no sequence is fitted and centred,
 * never rejected. Zod-free, like time.ts. Labels are editor i18n keys.
 */
import type { FrameRate } from './time.ts';

/** The rates a sequence can have (PRISM's custom format offers all six). */
export const SEQUENCE_RATES: readonly FrameRate[] = [
  { num: 24, den: 1 },
  { num: 25, den: 1 },
  { num: 30000, den: 1001 },
  { num: 30, den: 1 },
  { num: 50, den: 1 },
  { num: 60000, den: 1001 },
];

const r = (num: number, den = 1): FrameRate => ({ num, den });

export type Aspect = '16:9' | '9:16' | '4:5' | '1:1' | 'other';
export type FormatId = 'hd1080' | 'uhd' | 'hd720' | 'vertical' | 'portrait45' | 'square';

export interface FormatGroup {
  /** PRISM's preset id. */
  id: FormatId;
  width: number;
  height: number;
  aspect: Exclude<Aspect, 'other'>;
  /** Allowed rates in PRISM's order; the first is what a format change falls back to. */
  rates: readonly FrameRate[];
  /** i18n key of the label (e.g. "HD 1920 × 1080"). */
  label: string;
}

/** PRISM's format presets, landscape first. */
export const FORMAT_GROUPS: readonly FormatGroup[] = [
  { id: 'hd1080', width: 1920, height: 1080, aspect: '16:9', rates: [r(25), r(30000, 1001), r(24), r(50), r(60000, 1001)], label: 'format.hd1080' },
  { id: 'uhd', width: 3840, height: 2160, aspect: '16:9', rates: [r(25), r(30000, 1001), r(50)], label: 'format.uhd' },
  { id: 'hd720', width: 1280, height: 720, aspect: '16:9', rates: [r(50), r(60000, 1001)], label: 'format.hd720' },
  { id: 'vertical', width: 1080, height: 1920, aspect: '9:16', rates: [r(25), r(30000, 1001), r(30)], label: 'format.vertical' },
  { id: 'portrait45', width: 1080, height: 1350, aspect: '4:5', rates: [r(25), r(30000, 1001), r(30)], label: 'format.portrait45' },
  { id: 'square', width: 1080, height: 1080, aspect: '1:1', rates: [r(25), r(30000, 1001), r(30)], label: 'format.square' },
];

/** The shapes in the order the editor lists them, each with its i18n label key. */
export const ASPECTS: ReadonlyArray<{ aspect: Exclude<Aspect, 'other'>; label: string }> = [
  { aspect: '16:9', label: 'format.shape.landscape' },
  { aspect: '9:16', label: 'format.shape.vertical' },
  { aspect: '4:5', label: 'format.shape.portrait' },
  { aspect: '1:1', label: 'format.shape.square' },
];

/** Custom sizes PRISM accepts: even, 16–7680 wide and 16–4320 high. */
export const CUSTOM_SIZE_LIMITS = { minWidth: 16, maxWidth: 7680, minHeight: 16, maxHeight: 4320, even: true } as const;

type Size = { width: number; height: number };

/** The preset with exactly this size, or 'custom'. */
export function formatGroupOf(canvas: Size): FormatGroup | 'custom' {
  return FORMAT_GROUPS.find((g) => g.width === canvas.width && g.height === canvas.height) ?? 'custom';
}

/** The rates allowed for a canvas: its preset's, or all six for a custom size. */
export function ratesFor(canvas: Size): readonly FrameRate[] {
  const g = formatGroupOf(canvas);
  return g === 'custom' ? SEQUENCE_RATES : g.rates;
}

export const sameRate = (a: FrameRate, b: FrameRate): boolean => a.num * b.den === b.num * a.den;

/** Whether `rate` is one of `rates`, as a ratio (60000/2002 is 30000/1001). */
export const rateAllowed = (rate: FrameRate, rates: readonly FrameRate[]): boolean => rates.some((x) => sameRate(x, rate));

/** `rate` when the canvas allows it, otherwise the canvas's first allowed rate (PRISM's order). */
export function rateFor(canvas: Size, rate: FrameRate): FrameRate {
  const rates = ratesFor(canvas);
  return rateAllowed(rate, rates) ? rate : { ...rates[0]! };
}

const ASPECT_RATIOS: Array<[Exclude<Aspect, 'other'>, number]> = [
  ['16:9', 16 / 9],
  ['9:16', 9 / 16],
  ['4:5', 4 / 5],
  ['1:1', 1],
];

/** The shape of a canvas: one of PRISM's aspect ratios (within 1 %, so 1366×768 is 16:9), or 'other'. */
export function aspectOf(canvas: Size): Aspect {
  if (!(canvas.width > 0 && canvas.height > 0)) return 'other';
  const ratio = canvas.width / canvas.height;
  for (const [aspect, x] of ASPECT_RATIOS) if (Math.abs(ratio / x - 1) <= 0.01) return aspect;
  return 'other';
}

/** What makes a size one PRISM's custom format would not take: odd, or outside the limits; undefined when fine. */
export function customSizeProblem(canvas: Size): 'odd' | 'range' | undefined {
  const l = CUSTOM_SIZE_LIMITS;
  if (canvas.width < l.minWidth || canvas.width > l.maxWidth || canvas.height < l.minHeight || canvas.height > l.maxHeight) return 'range';
  if (canvas.width % 2 !== 0 || canvas.height % 2 !== 0) return 'odd';
  return undefined;
}

/** Thumbnail size for a canvas: the longest side 480, at the canvas's aspect (480×270 for 16:9, 270×480 for 9:16). */
export function thumbnailSize(canvas: Size): Size {
  const { width: w, height: h } = canvas;
  if (!(w > 0 && h > 0)) return { width: 480, height: 270 };
  return w >= h ? { width: 480, height: Math.max(1, Math.round((480 * h) / w)) } : { width: Math.max(1, Math.round((480 * w) / h)), height: 480 };
}
